<template>
    <lb-drawer
        data-testid="transaction-matching-config-new__mapping-master-drawer"
        :value="dialog"
        @input="$emit('update:dialog', $event)"
        :heading="item._id ? 'Update Mapping Master' : 'Add Mapping Master'"
        width="600"
        :loading="loading"
    >
        <template v-slot:body>
            <div>
                <v-row no-gutters>
                    <v-col cols="12" class="my-0 py-0">
                        <lb-dropdown
                            v-if="isV2Mode"
                            data-testid="transaction-matching-config-new__middleware-filecomparison-dropdown"
                            label="Dataset Combination"
                            :items="comparisonOptions"
                            itemtext="label"
                            itemvalue="value"
                            :value="item.filecomparison"
                            @input="$set(item, 'filecomparison', $event)"
                            :error="errorItem.filecomparison"
                            :attach="false"
                        />
                    </v-col>
                    <v-col cols="12" class="my-1 py-1">
                        <lb-string
                            data-testid="transaction-matching-config-new__middleware-filename-input"
                            v-model="item.middlewarename"
                            label="Middleware Name"
                            :error="errorItem.name"
                        />
                    </v-col>
                    <v-col cols="12" class="my-0 py-0">
                        <lb-dropdown
                            data-testid="transaction-matching-config-new__middleware-basefile-dropdown"
                            class="flux-grow-1 mr-1"
                            label="Link to base file"
                            :items="files"
                            itemtext="name"
                            itemvalue="_id"
                            v-model="item.basefile"
                            :error="errorItem.basefile"
                            :attach="false"
                        />
                    </v-col>
                    <v-col cols="6" class="my-0 py-0">
                        <lb-file
                            data-testid="transaction-matching-config-new__middleware-file-upload"
                            :value="fileValue"
                            :id="fileValue ? '' : existingFileId"
                            :existing-file-name="fileValue ? '' : existingFileLabel"
                            @input="$emit('update:fileValue', $event)"
                            label=""
                            class="py-0 my-0"
                            :drag="false"
                            hidedetails
                            :displayname="true"
                            accept=".xlsx,.xls,.xlsm,.xlsb,.csv"
                        />
                    </v-col>
                    <v-col cols="6" class="my-0 py-0">
                        <span class="mt-2 ml-3 error--text">
                            {{ errorItem.middFile }}
                        </span>
                    </v-col>
                </v-row>
            </div>
        </template>
        <template v-slot:actions>
            <v-spacer></v-spacer>
            <v-btn
                data-testid="transaction-matching-config-new__update-middleware-button"
                color="primary"
                class=""
                v-if="item._id"
                @click="$emit('update', item._id)"
            >
                <span>Update</span>
            </v-btn>
            <v-btn
                data-testid="transaction-matching-config-new__add-middleware-button"
                color="primary"
                class=""
                @click="$emit('add')"
                v-else
            >
                <span>Add</span>
            </v-btn>
        </template>
    </lb-drawer>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'MappingMasterDrawer',
    props: {
        dialog: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        item: {
            type: Object,
            required: true // middlewareObject
        },
        errorItem: {
            type: Object,
            default: () => ({})
        },
        files: {
            type: Array, // setupList.file
            default: () => []
        },
        fileValue: {
            // middFile
            default: null
        },
        fileConfigInfo: {
            type: Object,
            default: () => ({})
        },
        isV2Mode: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            resolvedUploadFileName: '',
        };
    },

    computed: {
        existingFileId() {
            const existing = this.item?.middlewarefile;
            if (Array.isArray(existing)) {
                const first = existing[0];
                if (!first) return '';
                if (typeof first === 'object') return String(first._id || first.id || '');
                return String(first);
            }
            if (existing && typeof existing === 'object') {
                return String(existing._id || existing.id || '');
            }
            return existing ? String(existing) : '';
        },
        existingFileLabel() {
            if (this.fileValue instanceof File) {
                return this.fileValue.name;
            }

            const directName = this.item?.uploadFileName
                || this.item?.filename
                || this.resolvedUploadFileName
                || '';
            if (directName) return directName;

            const path = this.item?.middlewarefilepath || this.item?.filepath || '';
            if (path) {
                const parts = String(path).split(/[/\\]/);
                return parts[parts.length - 1] || path;
            }

            const existing = this.item?.middlewarefile;
            if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
                return existing.uploadname || existing.filename || existing.originalname || existing.name || '';
            }
            if (Array.isArray(existing) && existing.length) {
                const first = existing[0];
                if (first && typeof first === 'object') {
                    return first.uploadname || first.filename || first.originalname || first.name || '';
                }
            }

            return '';
        },
        comparisonOptions() {
            const result = [];
            const entries = Object.entries(this.fileConfigInfo || {});
            for (let i = 0; i < entries.length; i++) {
                for (let j = i + 1; j < entries.length; j++) {
                    const [id1, cfg1] = entries[i];
                    const [id2, cfg2] = entries[j];
                    // Resolve display name: use matching file name or fallback to stored name
                    const file1 = (this.files || []).find(f => f._id === cfg1.file || f._id === id1);
                    const file2 = (this.files || []).find(f => f._id === cfg2.file || f._id === id2);
                    const label1 = file1 ? file1.name : (cfg1.name || id1);
                    const label2 = file2 ? file2.name : (cfg2.name || id2);
                    result.push({
                        label: `${label1} vs ${label2}`,
                        value: `${id1}_vs_${id2}`,
                    });
                }
            }
            return result;
        },
    },

    watch: {
        dialog(val) {
            if (val) {
                this._autoSelectFirstOption();
                this.resolveExistingUploadFileName();
            } else {
                this.resolvedUploadFileName = '';
            }
        },
        comparisonOptions(val) {
            if (val && val.length) this._autoSelectFirstOption();
        },
        existingFileId() {
            if (this.dialog) this.resolveExistingUploadFileName();
        },
    },

    methods: {
        ...mapActions('transactionMatching', ['fetchUploadFilenames']),
        _autoSelectFirstOption() {
            if (!this.isV2Mode) return;
            if (!this.item.filecomparison && this.comparisonOptions.length) {
                this.$set(this.item, 'filecomparison', this.comparisonOptions[0].value);
            }
        },

        async resolveExistingUploadFileName() {
            if (this.fileValue instanceof File) {
                this.resolvedUploadFileName = '';
                return;
            }

            const directName = this.item?.uploadFileName || this.item?.filename || '';
            if (directName) {
                this.resolvedUploadFileName = directName;
                return;
            }

            const path = this.item?.middlewarefilepath || this.item?.filepath || '';
            if (path) {
                const parts = String(path).split(/[/\\]/);
                this.resolvedUploadFileName = parts[parts.length - 1] || '';
                if (this.resolvedUploadFileName) return;
            }

            const id = this.existingFileId;
            if (!id) {
                this.resolvedUploadFileName = '';
                return;
            }

            const nameById = await this.fetchUploadFilenames({ fileIds: id });
            this.resolvedUploadFileName = nameById[id] || '';
        },
    },
};
</script>